import {GET} from '../modules/fetch.ts';
import {FetchRetryer} from '../fetchretryer.ts';
import {globalContainer} from '../container.ts';
import {buildDiffPreviewRoute} from '../components/AllSpiceDiff/AllSpiceDiffViewRouteBuilder.ts';
import {AllSpiceJsonLoader} from './allspicejson.ts';
import {mergePages} from '../allspice_app/mergePages.ts';
import {FILE_DIFF_STATUS, type FileDiffStatus} from '../allspice_app/types/file_tree.ts';
import {resolvePageName} from '../components/smart-review-utils.ts';

export type DesignReviewPage = {
  headPath: string;
  pageId: string;
  name: string;
  status: FileDiffStatus;
};

type SchematicFile = {
  ty: string;
  headPath: string;
  basePath: string;
  status: string;
};

type ProjectDiffNode = {
  ty: string;
  name: string;
  headPath: string;
  basePath: string;
  status: string;
  children?: ProjectDiffNode[];
};

type DiffPageEntry = {
  name: string;
  pageId: string;
  status?: FileDiffStatus;
  basePageId?: string;
};

/**
 * Lists the schematic pages of a Design Review in file and sheet order. It
 * asks crysknife for each schematic file, so it can be slow on a large design.
 * A file whose pages fail to load is left out.
 */
export async function fetchDesignReviewPages(owner: string, repo: string, pullIndex: string): Promise<DesignReviewPage[]> {
  const projectUrl = `${window.config.appSubUrl}/${owner}/${repo}/pulls/${pullIndex}/project`;
  const projectRes = await GET(projectUrl);
  if (!projectRes.ok) return [];

  const projectDiff = await projectRes.json();
  if (!projectDiff.nodes) return [];

  const headCommit = projectDiff.head_commit_id as string;
  const baseCommit = projectDiff.base_commit_id as string;

  const schFiles: SchematicFile[] = [];
  collectSchematicFiles(projectDiff.nodes as ProjectDiffNode[], schFiles);

  // A one-sheet-per-file format keeps its unchanged sheets in files the diff
  // never mentions, and in nodes the grouped response marks unchanged. Neither
  // has a diff to load pages from, so both take the convert JSON instead.
  const unchangedFiles = await unchangedSinglePageFiles(owner, repo, headCommit, schFiles);
  const diffFiles = schFiles.filter((sch) => !isUnchangedSinglePage(sch));

  // Per-file try/catch so a persistent crysknife error on one file (e.g. a
  // cached 500) doesn't mask pages that loaded fine from the other files.
  const results = await Promise.all(
    diffFiles.map(async (sch) => {
      try {
        return await fetchPagesForFile(owner, repo, headCommit, baseCommit, sch);
      } catch (err) {
        console.warn(`failed to load pages for ${sch.headPath}`, err);
        return [];
      }
    }),
  );
  const unchanged = await Promise.all(
    unchangedFiles.map(async (headPath) => {
      try {
        return await fetchPagesForUnchangedFile(owner, repo, headCommit, headPath);
      } catch (err) {
        console.warn(`failed to load pages for unchanged ${headPath}`, err);
        return [];
      }
    }),
  );
  return [...results.flat(), ...unchanged.flat()];
}

/**
 * Sheets of the project that this review does not touch, for formats that put
 * one sheet in each file.
 *
 * A multi-page format (OrCAD, System Capture, Cadence, DxDesigner) carries its
 * unchanged sheets inside a file the diff already lists, and they are merged in
 * from that file's convert JSON. Altium and KiCad put each sheet in its own
 * file, so an untouched sheet is an untouched *file*: absent from the diff, and
 * previously impossible to pick for review.
 */
async function unchangedSinglePageFiles(
  owner: string,
  repo: string,
  headCommit: string,
  schFiles: SchematicFile[],
): Promise<string[]> {
  // A path the diff loader still handles must not also load as unchanged.
  const changed = new Set(
    schFiles.filter((sch) => !isUnchangedSinglePage(sch)).map((sch) => sch.headPath),
  );
  const seen = new Set<string>();
  const unchanged: string[] = [];
  const offer = (path: string) => {
    if (!path || changed.has(path) || seen.has(path)) return;
    if (!isSinglePageSchematic(path)) return;
    seen.add(path);
    unchanged.push(path);
  };

  // The grouped project response names the documents a review does not touch,
  // so these need no lookup of their own.
  for (const sch of schFiles) {
    if (isUnchangedSinglePage(sch)) offer(sch.headPath);
  }

  const anchor = schFiles.find((sch) => sch.ty === 'sch-diff' && sch.headPath);
  // Without a single-page sheet there is no project to ask about, and nothing
  // this can add: a multi-page review already has its whole universe.
  if (!anchor) return unchanged;

  const projectUrl = AllSpiceJsonLoader.buildPrjUrl(
    globalContainer, owner, repo, headCommit, anchor.headPath,
  );
  let project: {documents?: Record<string, string>} | null = null;
  try {
    project = (await fetchJson(projectUrl)) as {documents?: Record<string, string>} | null;
  } catch (err) {
    // An enrichment, like the convert JSON above: without it the selector
    // still offers every changed sheet.
    console.warn(`project documents unavailable for ${anchor.headPath}; changed sheets only`, err);
    return unchanged;
  }

  for (const path of Object.values(project?.documents || {})) offer(path);
  return unchanged;
}

/** An untouched sheet of a one-sheet-per-file format: no diff to load from. */
function isUnchangedSinglePage(sch: SchematicFile): boolean {
  return sch.status === FILE_DIFF_STATUS.UNCHANGED && isSinglePageSchematic(sch.headPath);
}

function isSinglePageSchematic(path: string): boolean {
  const lower = path.toLowerCase();
  return lower.endsWith('.schdoc') || lower.endsWith('.kicad_sch');
}

async function fetchPagesForUnchangedFile(
  owner: string,
  repo: string,
  headCommit: string,
  headPath: string,
): Promise<DesignReviewPage[]> {
  const convertUrl = AllSpiceJsonLoader.buildUrl(globalContainer, owner, repo, headCommit, headPath);
  const convertJson = (await fetchJson(convertUrl)) as {pages?: {id: string; name: string}[]} | null;
  return (convertJson?.pages || []).map((page) => ({
    headPath,
    pageId: page.id || '',
    name: resolvePageName(page.name, headPath),
    status: FILE_DIFF_STATUS.UNCHANGED,
  }));
}

async function fetchJson(url: string | null): Promise<unknown> {
  if (!url) return null;
  const retryer = FetchRetryer.for(globalContainer, url, {
    fetchImmediately: true,
    skipCache: true,
  });
  await retryer.promise;
  if (!retryer.response) return null;
  return await retryer.response.json();
}

async function fetchPagesForFile(
  owner: string,
  repo: string,
  headCommit: string,
  baseCommit: string,
  sch: SchematicFile,
): Promise<DesignReviewPage[]> {
  const isAdded = sch.status === FILE_DIFF_STATUS.ADDED;
  const diffUrl = buildDiffPreviewRoute(
    globalContainer,
    owner, repo,
    'diff_page', headCommit, sch.headPath,
    '', '',
    isAdded ? '' : baseCommit,
    isAdded ? '' : sch.basePath,
  );

  const diffData = await fetchJson(diffUrl);
  const diffPages: DiffPageEntry[] = Array.isArray(diffData) ?
    diffData as DiffPageEntry[] :
    ((diffData as {headJson?: DiffPageEntry[]})?.headJson || []);

  // The diff only reports changed pages. For multi-page files (OrCAD/SysCap/
  // Cadence), the file's unchanged sheets live inside the same changed file, so
  // we merge the convert JSON's full head page universe to offer them too. This
  // mirrors the Changes-tab viewer (see allspice_app/store.ts). The convert JSON
  // is skipped for added/deleted files (no head-vs-base universe to merge) and
  // is unavailable for Altium, in which case mergePages falls back to the
  // diff-only (changed) page list. Single-page formats (KiCad/Altium .SchDoc)
  // are 'sch-diff'; their unchanged sheets are unchanged *files* absent from the
  // diff entirely, so they are out of scope here.
  const canHaveUnchangedPages =
    sch.ty === 'multi-page-diff' && !isAdded && sch.status !== FILE_DIFF_STATUS.DELETED;

  if (canHaveUnchangedPages) {
    const convertUrl = AllSpiceJsonLoader.buildUrl(
      globalContainer, owner, repo, headCommit, sch.headPath,
    );
    // The convert JSON is an enrichment used only to surface unchanged pages.
    // If it's unavailable (e.g. a format the converter can't describe returns
    // 404, which makes FetchRetryer reject), fall through to the changed-only
    // path below. We deliberately do NOT mergePages(diffPages, null) here:
    // mergePages defaults missing statuses to UNCHANGED, and since unchanged
    // pages are hidden by default in the DRCY page selector, a degraded/empty
    // convert payload could hide genuinely changed sheets and leave Start disabled.
    let convertJson: unknown = null;
    try {
      convertJson = await fetchJson(convertUrl);
    } catch (err) {
      console.warn(`convert JSON unavailable for ${sch.headPath}; showing changed pages only`, err);
    }
    if (convertJson) {
      const merged = mergePages(
        diffPages,
        convertJson as {pages?: {id: string; name: string}[]} | null,
      );
      return merged.map((page) => ({
        headPath: sch.headPath,
        pageId: page.pageId || '',
        name: resolvePageName(page.name, sch.headPath),
        status: page.status,
      }));
    }
  }

  // Changed-only path: every page returned by the diff is a change. Absent an
  // explicit status (older renderers / a degraded diff payload), treat it as
  // modified so it stays visible and selectable. Unchanged pages are hidden
  // by default, so defaulting to UNCHANGED here could disable Start.
  return diffPages.map((page) => ({
    headPath: sch.headPath,
    pageId: page.pageId || '',
    name: resolvePageName(page.name, sch.headPath),
    status: page.status ?? FILE_DIFF_STATUS.MODIFIED,
  }));
}

function collectSchematicFiles(nodes: ProjectDiffNode[], result: SchematicFile[]) {
  for (const node of nodes) {
    if (node.ty === 'sch-diff' || node.ty === 'multi-page-diff') {
      result.push({
        ty: node.ty,
        headPath: node.headPath || '',
        basePath: node.basePath || '',
        status: node.status || '',
      });
    }
    if (node.children) {
      collectSchematicFiles(node.children, result);
    }
  }
}
